/**
 * CropTool Component
 * Feature: 006-critique-editing
 *
 * Interactive crop tool using react-image-crop library.
 * Provides aspect ratio selector and drag handles for defining crop region.
 */

import { useState, useCallback, useRef } from 'react'
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import type { CropData } from '../../../types/edit.types'
import { SurfaceProvider } from '@/theme/SurfaceContext'
import { AdaptivePhoto } from '../../photos/AdaptivePhoto'

/**
 * Represents the visible bounds of an image within an object-contain element.
 * When an image uses object-contain, it's scaled to fit the container while
 * maintaining aspect ratio, resulting in letterboxing (empty space around the image).
 */
interface VisibleImageBounds {
  /** X offset from the element's left edge to the visible image */
  offsetX: number
  /** Y offset from the element's top edge to the visible image */
  offsetY: number
  /** Width of the visible image area */
  width: number
  /** Height of the visible image area */
  height: number
}

/**
 * Calculate the visible image bounds within an object-contain img element.
 *
 * With object-contain, the image is scaled to fit within the element while
 * maintaining its aspect ratio. This creates "letterboxing" where the image
 * doesn't fill the entire element. This function calculates where the actual
 * visible image is positioned within the element.
 */
function getVisibleImageBounds(img: HTMLImageElement): VisibleImageBounds {
  const elementWidth = img.width
  const elementHeight = img.height
  const naturalWidth = img.naturalWidth
  const naturalHeight = img.naturalHeight

  // Calculate scale factor (object-contain uses the smaller scale to fit)
  const scaleX = elementWidth / naturalWidth
  const scaleY = elementHeight / naturalHeight
  const scale = Math.min(scaleX, scaleY)

  // Calculate visible image dimensions
  const visibleWidth = naturalWidth * scale
  const visibleHeight = naturalHeight * scale

  // Calculate centering offsets (object-contain centers the image)
  const offsetX = (elementWidth - visibleWidth) / 2
  const offsetY = (elementHeight - visibleHeight) / 2

  return {
    offsetX,
    offsetY,
    width: visibleWidth,
    height: visibleHeight,
  }
}

export interface CropToolProps {
  /** Photo object with variants for adaptive resolution */
  photo: import('../../../types/photo').Photo
  /** Initial crop data (optional) */
  initialCrop?: CropData
  /** Callback when crop changes */
  onChange?: (crop: CropData) => void
  /** Callback when crop is completed (user releases mouse) */
  onCropComplete?: (crop: CropData) => void
  /** Enable debug overlay showing variant selection and dimensions */
  showDebug?: boolean
  /** Additional class names */
  className?: string
}

type AspectRatioOption =
  | 'free'
  | '1:1'
  | '3:4' | '4:3'
  | '2:3' | '3:2'
  | '9:16' | '16:9'
  | '24:65' | '65:24'

const ASPECT_RATIO_VALUES: Record<AspectRatioOption, number | undefined> = {
  'free': undefined,
  '1:1': 1,
  '3:4': 3 / 4,      // Portrait
  '4:3': 4 / 3,      // Landscape
  '2:3': 2 / 3,      // Portrait
  '3:2': 3 / 2,      // Landscape
  '9:16': 9 / 16,    // Portrait
  '16:9': 16 / 9,    // Landscape
  '24:65': 24 / 65,  // xpan Portrait
  '65:24': 65 / 24,  // xpan Landscape
}

const ASPECT_RATIO_GROUPS = [
  { label: 'Free', ratios: ['free'] },
  { label: 'Square', ratios: ['1:1'] },
  { label: '4:3', ratios: ['3:4', '4:3'] },
  { label: '3:2', ratios: ['2:3', '3:2'] },
  { label: '16:9', ratios: ['9:16', '16:9'] },
  { label: 'xpan', ratios: ['24:65', '65:24'] },
] as const

/**
 * Convert percentage-based CropData to pixel-based Crop for react-image-crop.
 * Takes into account the visible image bounds when using object-contain.
 *
 * @param cropData - Percentage-based crop data (0-100 values relative to image)
 * @param bounds - The visible image bounds within the object-contain element
 */
function cropDataToCrop(cropData: CropData, bounds: VisibleImageBounds): Crop {
  return {
    unit: 'px',
    // Convert percentage to pixels relative to visible image, then add offset
    x: bounds.offsetX + (cropData.x / 100) * bounds.width,
    y: bounds.offsetY + (cropData.y / 100) * bounds.height,
    width: (cropData.width / 100) * bounds.width,
    height: (cropData.height / 100) * bounds.height,
  }
}

/**
 * Convert pixel-based Crop to percentage-based CropData.
 * Takes into account the visible image bounds when using object-contain.
 * Clamps values to 0-100 range to ensure crop stays within image bounds.
 *
 * @param crop - Pixel-based crop from ReactCrop (coordinates relative to element)
 * @param bounds - The visible image bounds within the object-contain element
 * @param aspectRatio - The selected aspect ratio option
 */
function cropToCropData(crop: PixelCrop, bounds: VisibleImageBounds, aspectRatio: AspectRatioOption): CropData {
  // Calculate the actual aspect ratio from pixel dimensions
  // This is especially important for 'free' crops where we need to preserve the exact shape
  const computedAspectRatio = crop.width / crop.height

  // Convert pixel coordinates to percentages relative to visible image
  // Subtract offset to get position relative to visible image, then convert to percentage
  const x = ((crop.x - bounds.offsetX) / bounds.width) * 100
  const y = ((crop.y - bounds.offsetY) / bounds.height) * 100
  const width = (crop.width / bounds.width) * 100
  const height = (crop.height / bounds.height) * 100

  // Clamp values to valid 0-100 range (prevents selecting outside image bounds)
  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y)),
    width: Math.max(0, Math.min(100 - Math.max(0, x), width)),
    height: Math.max(0, Math.min(100 - Math.max(0, y), height)),
    aspectRatio,
    computedAspectRatio,
  }
}

/**
 * Interactive crop tool with aspect ratio selector
 *
 * @example
 * <CropTool
 *   photo={photo}
 *   onChange={(crop) => console.log('Crop changed:', crop)}
 *   onCropComplete={(crop) => console.log('Crop completed:', crop)}
 * />
 */
export function CropTool({
  photo,
  initialCrop,
  onChange,
  onCropComplete,
  showDebug = false,
  className = '',
}: CropToolProps): JSX.Element {
  const [aspectRatio, setAspectRatio] = useState<AspectRatioOption>(
    initialCrop?.aspectRatio || 'free'
  )
  const [crop, setCrop] = useState<Crop | undefined>(() => {
    if (initialCrop) {
      // We'll convert this once we have image dimensions
      return undefined
    }
    return {
      unit: '%',
      x: 10,
      y: 10,
      width: 80,
      height: 80,
    }
  })
  const [visibleBounds, setVisibleBounds] = useState<VisibleImageBounds | null>(null)
  const [currentCropData, setCurrentCropData] = useState<CropData | undefined>(initialCrop)
  const imageContainerRef = useRef<HTMLDivElement>(null)

  // Handle image load from AdaptivePhoto - get the img element and recalculate crop
  const handleAdaptivePhotoLoad = useCallback((_dimensions: { width: number; height: number }) => {
    // Find the img element in the container
    if (imageContainerRef.current) {
      const img = imageContainerRef.current.querySelector('img')
      if (img) {
        // Recalculate crop from currentCropData whenever image loads (handles resolution changes)
        // Use requestAnimationFrame to ensure layout is complete
        requestAnimationFrame(() => {
          // Calculate the visible image bounds (accounting for object-contain letterboxing)
          const bounds = getVisibleImageBounds(img)
          setVisibleBounds(bounds)

          if (currentCropData) {
            const pixelCrop = cropDataToCrop(currentCropData, bounds)
            setCrop(pixelCrop)
          }
        })
      }
    }
  }, [currentCropData])

  // Handle crop change
  const handleCropChange = useCallback((newCrop: Crop) => {
    setCrop(newCrop)

    // Call onChange callback with percentage-based CropData
    if (onChange && visibleBounds && newCrop.width && newCrop.height) {
      const cropData = cropToCropData(
        newCrop as PixelCrop,
        visibleBounds,
        aspectRatio
      )
      setCurrentCropData(cropData) // Store crop data for recalculation on resolution change
      onChange(cropData)
    }
  }, [onChange, visibleBounds, aspectRatio])

  // Handle crop complete (user releases mouse)
  const handleCropComplete = useCallback((completedCrop: PixelCrop) => {
    if (onCropComplete && visibleBounds && completedCrop.width && completedCrop.height) {
      const cropData = cropToCropData(
        completedCrop,
        visibleBounds,
        aspectRatio
      )
      setCurrentCropData(cropData) // Store crop data for recalculation on resolution change
      onCropComplete(cropData)
    }
  }, [onCropComplete, visibleBounds, aspectRatio])

  // Handle aspect ratio change
  const handleAspectRatioChange = useCallback((newAspectRatio: AspectRatioOption) => {
    setAspectRatio(newAspectRatio)

    // If there's a current crop, adjust it to match new aspect ratio
    if (crop && visibleBounds) {
      const aspectValue = ASPECT_RATIO_VALUES[newAspectRatio]

      if (aspectValue) {
        // Calculate new crop maintaining center point
        const centerX = crop.x + crop.width / 2
        const centerY = crop.y + crop.height / 2

        let newWidth = crop.width
        let newHeight = crop.width / aspectValue

        // Ensure height fits within visible image area
        const maxHeight = visibleBounds.height
        if (newHeight > maxHeight) {
          newHeight = maxHeight * 0.8
          newWidth = newHeight * aspectValue
        }

        // Ensure width fits within visible image area
        const maxWidth = visibleBounds.width
        if (newWidth > maxWidth) {
          newWidth = maxWidth * 0.8
          newHeight = newWidth / aspectValue
        }

        // Clamp position to keep crop within visible image bounds
        const minX = visibleBounds.offsetX
        const minY = visibleBounds.offsetY
        const maxX = minX + visibleBounds.width - newWidth
        const maxY = minY + visibleBounds.height - newHeight

        const newCrop: Crop = {
          unit: 'px',
          x: Math.max(minX, Math.min(maxX, centerX - newWidth / 2)),
          y: Math.max(minY, Math.min(maxY, centerY - newHeight / 2)),
          width: newWidth,
          height: newHeight,
        }

        setCrop(newCrop)
        handleCropChange(newCrop)
      }
    }
  }, [crop, visibleBounds, handleCropChange])

  return (
    <SurfaceProvider level="base" theme="light">
      <div className={`crop-tool w-full h-full flex flex-col ${className}`}>
        {/* Aspect Ratio Selector */}
        <div className="mb-4 space-y-2">
          <span className="text-sm font-medium text-foreground">Aspect Ratio:</span>
          <div className="flex flex-wrap gap-2">
            {ASPECT_RATIO_GROUPS.map((group) => (
              <div key={group.label} className="flex gap-1 items-center">
                {group.ratios.length === 1 ? (
                  <button
                    type="button"
                    onClick={() => handleAspectRatioChange(group.ratios[0] as AspectRatioOption)}
                    className={`
                      px-3 py-1 text-sm rounded border
                      ${
                        aspectRatio === group.ratios[0]
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border hover:bg-muted/20'
                      }
                    `}
                  >
                    {group.label}
                  </button>
                ) : (
                  <>
                    <span className="text-xs text-muted mr-1">{group.label}:</span>
                    {group.ratios.map((ratio) => (
                      <button
                        type="button"
                        key={ratio}
                        onClick={() => handleAspectRatioChange(ratio as AspectRatioOption)}
                        className={`
                          px-2 py-1 text-xs rounded border
                          ${
                            aspectRatio === ratio
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-foreground border-border hover:bg-muted/20'
                          }
                        `}
                        title={ratio.includes(':') && parseInt(ratio.split(':')[0]) < parseInt(ratio.split(':')[1]) ? 'Portrait' : 'Landscape'}
                      >
                        {ratio}
                      </button>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Crop Area - Container with EXPLICIT dimensions so AdaptivePhoto can measure it */}
        <div
          className="relative flex items-center justify-center w-full flex-1 min-h-0"
          style={{ height: 'calc(100vh - 360px)', minHeight: '300px' }}
        >
          <ReactCrop
            crop={crop}
            onChange={handleCropChange}
            onComplete={handleCropComplete}
            aspect={ASPECT_RATIO_VALUES[aspectRatio]}
          >
            {/* Container with explicit dimensions for AdaptivePhoto variant selection */}
            <div
              ref={imageContainerRef}
              style={{
                width: 'calc(100vw - 120px)',
                height: 'calc(100vh - 360px)',
              }}
            >
              <AdaptivePhoto
                photo={photo}
                alt={photo.description || 'Crop preview'}
                onLoad={handleAdaptivePhotoLoad}
                showDebug={showDebug}
                className="w-full h-full"
                imgClassName="w-full h-full object-contain block"
              />
            </div>
          </ReactCrop>
        </div>

        {/* Crop Info (for debugging/display) */}
        {crop && visibleBounds && (
          <div className="mt-2 text-xs text-muted">
            Crop: {Math.round(((crop.x - visibleBounds.offsetX) / visibleBounds.width) * 100)}% x {Math.round(((crop.y - visibleBounds.offsetY) / visibleBounds.height) * 100)}%,
            Size: {Math.round((crop.width / visibleBounds.width) * 100)}% × {Math.round((crop.height / visibleBounds.height) * 100)}%
          </div>
        )}
      </div>
    </SurfaceProvider>
  )
}

export default CropTool
